import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { ScrollArea } from "./ui/scroll-area";
import { Calendar, Clock, Music, Users, Check, Bell, Loader2, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function PendingSchedulesModal({ open, onClose }) {
  const [pendingSchedules, setPendingSchedules] = useState([]);
  const [personName, setPersonName] = useState("");
  const [loading, setLoading] = useState(true);
  const [confirmingId, setConfirmingId] = useState(null);

  useEffect(() => {
    if (open) {
      fetchPendingSchedules();
    }
  }, [open]);

  const fetchPendingSchedules = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/my-pending-schedules`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setPendingSchedules(data.pending_schedules || []);
        setPersonName(data.person_name || "");
      }
    } catch (error) {
      console.error("Error fetching pending schedules:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (scheduleId) => {
    try {
      setConfirmingId(scheduleId);
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_URL}/api/my-pending-schedules/confirm/${scheduleId}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        toast.success("Presença confirmada!", {
          description: `${data.event_name}`,
        });
        
        // Remove from list
        setPendingSchedules((prev) =>
          prev.filter((s) => s.schedule_id !== scheduleId)
        );
        
        // Close modal if no more pending schedules
        if (pendingSchedules.length <= 1) {
          setTimeout(() => onClose(), 1000);
        }
      } else {
        toast.error("Erro ao confirmar presença");
      }
    } catch (error) {
      console.error("Error confirming schedule:", error);
      toast.error("Erro ao confirmar presença");
    } finally {
      setConfirmingId(null);
    }
  };

  const handleConfirmAll = async () => {
    for (const schedule of pendingSchedules) {
      await handleConfirm(schedule.schedule_id);
      // Small delay between confirmations
      await new Promise((resolve) => setTimeout(resolve, 300));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("pt-BR", {
        weekday: "short",
        day: "2-digit",
        month: "short",
      });
    } catch {
      return dateStr;
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] max-w-md max-h-[85vh] p-0 flex flex-col rounded-xl overflow-hidden">
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-orange-500 to-amber-500 px-4 py-5 text-white">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2 rounded-full">
              <Bell className="h-5 w-5" />
            </div>
            <div>
              <DialogTitle className="text-lg font-semibold text-white">
                Escalas Pendentes
              </DialogTitle>
              <DialogDescription className="text-white/80 text-sm">
                {loading
                  ? "Verificando suas escalas..."
                  : pendingSchedules.length > 0
                  ? `Você tem ${pendingSchedules.length} escala${pendingSchedules.length > 1 ? "s" : ""} aguardando confirmação`
                  : "Nenhuma escala pendente"}
              </DialogDescription>
            </div>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
          </div>
        ) : pendingSchedules.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <div className="bg-green-100 p-4 rounded-full mb-4">
              <Check className="h-8 w-8 text-green-600" />
            </div>
            <p className="text-lg font-medium text-gray-800">Tudo certo!</p>
            <p className="text-sm text-gray-500 mt-1">
              Você não tem escalas pendentes de confirmação
            </p>
            <Button className="mt-6" onClick={onClose}>
              Fechar
            </Button>
          </div>
        ) : (
          <>
            <ScrollArea className="flex-1 max-h-[50vh]">
              <div className="p-4 space-y-3">
                {pendingSchedules.map((schedule) => (
                  <div
                    key={schedule.schedule_id}
                    className="bg-white border rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow"
                  >
                    {/* Event info */}
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-800 truncate">
                          {schedule.event_name || schedule.schedule_name}
                        </h3>
                        <p className="text-sm text-gray-500 truncate">
                          {schedule.ministry_name}
                        </p>
                      </div>
                      <Badge
                        variant="outline"
                        className="flex-shrink-0 text-orange-600 border-orange-300 bg-orange-50"
                      >
                        Pendente
                      </Badge>
                    </div>

                    {/* Details */}
                    <div className="flex flex-wrap gap-3 text-sm text-gray-600 mb-4">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3.5 w-3.5 text-gray-400" />
                        {formatDate(schedule.event_date)}
                      </span>
                      {schedule.event_time && (
                        <span className="flex items-center gap-1">
                          <Clock className="h-3.5 w-3.5 text-gray-400" />
                          {schedule.event_time}
                        </span>
                      )}
                      {schedule.function_name && (
                        <span className="flex items-center gap-1">
                          <Users className="h-3.5 w-3.5 text-gray-400" />
                          {schedule.function_name}
                        </span>
                      )}
                    </div>

                    {/* Arrival time if set */}
                    {schedule.hora_voluntario && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
                        <p className="text-xs text-amber-700 flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          Chegar às <strong>{schedule.hora_voluntario}</strong>
                        </p>
                      </div>
                    )}

                    {/* Confirm button */}
                    <Button
                      className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
                      onClick={() => handleConfirm(schedule.schedule_id)}
                      disabled={confirmingId === schedule.schedule_id}
                    >
                      {confirmingId === schedule.schedule_id ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Confirmando...
                        </>
                      ) : (
                        <>
                          <Check className="h-4 w-4 mr-2" />
                          Confirmar Presença
                        </>
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            </ScrollArea>

            {/* Footer with actions */}
            <div className="border-t p-4 bg-gray-50 space-y-2">
              {pendingSchedules.length > 1 && (
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={handleConfirmAll}
                  disabled={confirmingId !== null}
                >
                  <Check className="h-4 w-4 mr-2" />
                  Confirmar Todas ({pendingSchedules.length})
                </Button>
              )}
              <Button variant="ghost" className="w-full" onClick={onClose}>
                Confirmar depois
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Hook to check for pending schedules
export function usePendingSchedules() {
  const [hasPending, setHasPending] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [checked, setChecked] = useState(false);

  const checkPendingSchedules = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const response = await fetch(`${API_URL}/api/my-pending-schedules`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const count = data.pending_schedules?.length || 0;
        setPendingCount(count);
        setHasPending(count > 0);
      }
    } catch (error) {
      console.error("Error checking pending schedules:", error);
    } finally {
      setChecked(true);
    }
  };

  useEffect(() => {
    checkPendingSchedules();
  }, []);

  return { hasPending, pendingCount, checked, refetch: checkPendingSchedules };
}
